import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import { NavigateFunction } from 'react-router';
import { IDataRoom } from 'interfaces';
import { IRoomInite } from 'interfaces/IInvitations';
import { adminPermissions, resetUserPermissions, setDefaultUserPermissions, setRooms, setSelectedDataRoom, setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { RootState } from 'store';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { setIsProfileOpen, setTriggerNdaLeave, setUnlinkModalState, setUpdateAfterDeleteTimer } from 'store/slices/windowStateSlice';
import { clearAllChatData } from 'store/slices/chatsSlice';
import { resetAppliedFilters, setCurrentFolder, setDocuments } from 'store/slices/dataDocumentsSlice';
import { setGroupChoosen, setGroups, setUserAndData } from 'store/slices/dataUsersSlice';
import { setDataForPermissionTable, setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { resetOrgsAndSides } from 'store/slices/dataSidesSlice';
import { setMessagesToLocalStorage, createChat } from './chatCreator';
import { removeItemsFromLocalStorage } from 'helpers/cleanLocalStorage';
import { setAllLogs, setLogs } from 'store/slices/dataLogsSlice';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { athena_state } from 'helpers/oauth2';

const client_vk_id = window.REACT_APP_VK_CLIENT_ID !== 'REPLACE_REACT_APP_VK_CLIENT_ID' ? window.REACT_APP_VK_CLIENT_ID : process.env.REACT_APP_VK_CLIENT_ID || '';
const client_ya_id = window.REACT_APP_YA_CLIENT_ID !== 'REPLACE_REACT_APP_YA_CLIENT_ID' ? window.REACT_APP_YA_CLIENT_ID : process.env.REACT_APP_YA_CLIENT_ID || '';
const yandex_client_secret = window.REACT_APP_YA_CLIENT_SECRET !== 'REPLACE_REACT_APP_YA_CLIENT_SECRET' ? window.REACT_APP_YA_CLIENT_SECRET : process.env.REACT_APP_YA_CLIENT_SECRET;

export const prepareRooms = (available_rooms: IDataRoom[]) => {
  return available_rooms
    .map((room) => ({
      ...room,
      created_at: Date.parse(String(room.created_at)),
      last_action_timestamp: Date.parse(String(room.last_action_timestamp || room.created_at)),
    }))
    .sort((a, b) => b.last_action_timestamp - a.last_action_timestamp);
};

export const fetchUserData = createAsyncThunk('dataUser/fetchAll', async (props: {navigate: NavigateFunction, t: TFunction}, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const { messageApi } = state.windowState;
  const { dispatch } = thunkAPI;

  try {
    const userInfo = await api.getUserInfo();
    const isNeedToCheckProfile = localStorage.getItem('need_to_ckeck_profile');
    if (isNeedToCheckProfile === 'true') {
      dispatch(setIsProfileOpen(true));
      messageApi?.warning(props.t('login.external.needToCheckProfile'), 10);
      localStorage.removeItem('need_to_ckeck_profile');
    } else if (!userInfo.data.first_name || !userInfo.data.last_name || !userInfo.data.phone_number) {
      dispatch(setIsProfileOpen(true));
      messageApi?.warning(props.t('login.external.needToFillFields'), 10);
    }
    return userInfo.data;
  } catch (e) {
    return thunkAPI.rejectWithValue('Error while retrieving user data');
  }
});

export const fetchUserRooms = createAsyncThunk('userData/fetchUserRooms', async (props: {}, thunkAPI) => {
  try {
    const userRooms = await api.getUserRooms();
    let invites: IRoomInite[] = [];

    try {      
      const userInvites = await api.getInvitesByUser();      
      const idsRooms = userRooms.data?.map(room => room.id);
      invites = userInvites.data.filter(invite => !idsRooms.includes(invite?.room?.id) && invite.invite_status !== 2 && invite?.room_id);
    } catch (e) {}    
    
    const invitesToRooms: IDataRoom[] = invites
      .map((invite) => ({
        ...invite.room,
        invite_status: invite.invite_status,
        is_expired: invite.is_expired,
        last_action_timestamp: null,
        lives_to: null,
        created_at: null,
        invite_id: invite.id,
        invited_at: invite.created_at
      }));
    
    return prepareRooms([...invitesToRooms, ...userRooms.data]);
  } catch (e) {
    return thunkAPI.rejectWithValue("Error while retrieving user's rooms");
  }
});

export const fetchUserPermissions = createAsyncThunk(
  'permissionsUser/fetchAll',
  async (props: { idRoom: string }, thunkAPI) => {
    try {
      const userPermissions = await api.getUserPermissions(props.idRoom);
      return userPermissions.data;
    } catch (e) {
      return thunkAPI.rejectWithValue('Download failed');
    }
  }
);

export const addExternalAuth = createAsyncThunk(
  'addNewAuth/externalAuth',
  async (props: {
    params: {code: string; state: string, device_id?: string, cid?: string},
    auth_name: string,
    t: TFunction,
  }, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const { messageApi } = state.windowState;
    const { dispatch } = thunkAPI;

    const code_verifier = String(localStorage.getItem('code_verifier'));
    const client_secret = yandex_client_secret;

    let right_pkce_auth_data: any;
    if (props.params.device_id) {
      const redirect_uri = window.location.origin;
      right_pkce_auth_data = { ...props.params, code_verifier, redirect_uri, client_id: client_vk_id }
    } else {
      const device_id = localStorage.getItem('device_id');
      const device_name = `browser_${athena_state}`;
      right_pkce_auth_data = {...props.params, code_verifier, client_id: client_ya_id, device_id, device_name, client_secret}
    }
    
    if (props.params.state === athena_state) {
      try {
        await api.addExternalAuth({
          service_name: props.auth_name,
          pkce_auth_data: right_pkce_auth_data,
        });
        dispatch(setIsProfileOpen(true));
        messageApi?.success(props.t('login.external.suсcessAddExternal', {name: props.t(`login.external.btns.${props.auth_name}`)}));
      } catch (error: any) {
        console.error(error);
        if (error?.response?.data?.message === 'External auth service already registered for other account') {
          messageApi?.error(props.t('login.external.errorAlreadyRegistered', {name: props.t(`login.external.btns.${props.auth_name}`)}), 12);
        } else if (error?.response?.data?.message === `Current user emails doesn't contains in OAuth service's emails list`) {
          messageApi?.error(props.t('login.external.errorAddExternalAnotherEmail', {name: props.t(`login.external.btns.${props.auth_name}`)}), 15);
        } else {
          messageApi?.error(props.t('login.external.errorAddExternal', {name: props.t(`login.external.btns.${props.auth_name}`)}));
        }
      } finally {
        localStorage.removeItem('code_verifier');
      }
    }
  }
);

export const deleteExternalAuth = createAsyncThunk(
  'deleteExternalAuth/externalAuth',
  async (props: {
    externalState: any[];
    t: TFunction;
    setExternalState: React.Dispatch<React.SetStateAction<any[]>>
  }, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const { messageApi, unlinkModalState } = state.windowState;
    const { dispatch } = thunkAPI;
    try {
      dispatch(setUnlinkModalState({...unlinkModalState, in_progress: true}));
      await api.deleteExternalAuth(props.externalState.find(el => String(el.service_name)?.toLocaleLowerCase() === unlinkModalState.service_name)?.id);
      messageApi?.success(props.t('login.external.suсcessUnlinkExternal', {name: props.t(`login.external.btns.${unlinkModalState.service_name}`)}), 10);
      props.setExternalState && props.setExternalState(prev => ([...prev.filter((el: any) => el?.service_name !== unlinkModalState.service_name)]));
    } catch (e) {
      messageApi?.error(props.t('login.external.errorUnlinkExternal', {name: props.t(`login.external.btns.${unlinkModalState.service_name}`)}), 10);
    } finally {
      dispatch(setUnlinkModalState({open: false, service_name: '', in_progress: false}))
    }
  }
);

export const updateRoomsBySockets = createAsyncThunk(
  'updateRoomsBySockets/update',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const { dispatch } = thunkAPI;
    const { updateAfterDeleteTimer } = state.windowState;

    if (updateAfterDeleteTimer) window.clearInterval(updateAfterDeleteTimer);
    dispatch(setUpdateAfterDeleteTimer(setTimeout(() => {
      dispatch(updateRooms());
    }, 3000)));
  }
);

export const updateRooms = createAsyncThunk('update/rooms', async (payload, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const { dataRoom } = state.dataRoom;
  const { dispatch } = thunkAPI;
  
  dispatch(setUpdateAfterDeleteTimer(null));
  try {
    const userAvailableRooms = await api.getUserRooms();
    let invites: IRoomInite[] = [];
    try {
      const userInvites = await api.getInvitesByUser();
      const idsRooms = userAvailableRooms.data.map(room => room.id);
      invites = userInvites.data.filter(invite => !idsRooms.includes(invite?.room?.id) && invite.invite_status !== 2 && invite?.room_id);
    } catch (e) {}
    
    const invitesToRooms: IDataRoom[] = invites
      .map((invite) => ({
        ...invite.room,
        invite_status: invite.invite_status,
        is_expired: invite.is_expired,
        last_action_timestamp: null,
        lives_to: null,
        created_at: null,
        invite_id: invite.id,
        invited_at: invite.created_at
      }));
    dispatch(setRooms(prepareRooms([...invitesToRooms, ...userAvailableRooms.data])));

    if (dataRoom) {
      const currentRoom = userAvailableRooms.data.find(room => room.id === dataRoom?.id);
      if (currentRoom?.id === dataRoom?.id) {
        const roomWithFreshData = { 
          ...dataRoom,
          documents_amount: currentRoom?.documents_amount,
          new_count: currentRoom?.new_count,
          unviewed_amount: currentRoom?.unviewed_amount,
          description: currentRoom?.description,
          rooms_constraints: {
            ...currentRoom?.rooms_constraints,
          }
        } as IDataRoom;

        dataRoom && dispatch(setDataRoom(roomWithFreshData));
      }
    };
  } catch (err) {
    console.log(err);
  };
});

export const clearReduxFunction = createAsyncThunk(
  'clearReduxFunction/delete',
  async (props: { }, thunkAPI) => {
    try {
      const { dispatch } = thunkAPI;
      dispatch(setTriggerNdaLeave(false));
      dispatch(setDataRoom(null));
      dispatch(clearAllChatData());
      dispatch(setDocuments({ documents: [] }));
      dispatch(resetUserPermissions());
      dispatch(setUserAndData({ users: [], dataUserTable: [] }));
      dispatch(setGroups([]));
      dispatch(setGroupChoosen(null));
      dispatch(setCurrentFolder([]));
      dispatch(setDataForPermissionTable({ documents: [] }));
      dispatch(setUserPermissions(null));
      dispatch(resetOrgsAndSides());
      dispatch(resetAppliedFilters());
      dispatch(setSelectedDataRoom(undefined));
      dispatch(setMessagesToLocalStorage());
      dispatch(setAllLogs([]));
      dispatch(setLogs([]));
      removeItemsFromLocalStorage();
    } catch (e) {
      console.log('Clearing redux ended with error:', e);
    }
  }
);

type UpdateRoomStateWithFetchParams = {
  newRoom: any;
  tariffData?: any;
  navigate: NavigateFunction;
  t: TFunction;
};

export const updateRoomStateAfterCreation = createAsyncThunk(
  'userData/updateRoomStateAfterCreation',
  async (props: UpdateRoomStateWithFetchParams, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { newRoom, tariffData, navigate, t } = props;

    try {
      const state = thunkAPI.getState() as RootState;
      const { userData, available_rooms } = state.userData;
      const { messageApi } = state.windowState;

      if (!userData?.id) {
        return thunkAPI.rejectWithValue('User data not loaded');
      }

      dispatch(setUserAndData({ dataUserTable: [], users: [] }));

      dispatch(
        createChat({
          data: {
            entity_type: 1,
            entity_id: newRoom.id,
            room_id: newRoom.id!,
            title: newRoom.name,
            members_ids: [userData.id],
          },
          ownerId: userData.id,
        })
      );

      let endedNewPeriod: string | undefined;
      if (tariffData) {
        const duration = tariffData.tariff_duration || tariffData.duration;
        const unit = tariffData.tariff_duration_unit || tariffData.duration_unit;

        switch (unit) {
          case 'DAY':
            endedNewPeriod = dayjs.utc().add(duration, 'day').format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
            break;
          case 'MONTH':
            endedNewPeriod = dayjs.utc().add(duration, 'month').format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
            break;
          case 'YEAR':
            endedNewPeriod = dayjs.utc().add(duration, 'year').format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
            break;
          default:
            endedNewPeriod = dayjs.utc().add(1, 'month').format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
        }
      }

      const UTCDate = dayjs().valueOf();
      const parsedDateRoom: IDataRoom = {
        ...newRoom,
        lives_to: null,
        last_action_timestamp: UTCDate,
        created_at: Date.parse(String(newRoom.created_at)),
        permissions: adminPermissions,
        documents_amount: 0,
        is_new: true,
        system_status: 'new',
        rooms_constraints: { members_amount: 1 },
        tariff: tariffData && endedNewPeriod
          ? { ...tariffData, status: 'new', tariff_ends_at: endedNewPeriod }
          : null,
      };
      dispatch(
        setRooms(
          available_rooms[0]
            ? [parsedDateRoom, ...available_rooms]
            : [parsedDateRoom]
        )
      );

      messageApi?.success(t('Header.success.created'));

      navigate(`/room/${newRoom.id}/documents`);
      dispatch(setGroups([]));
      await dispatch(fetchUserPermissions({ idRoom: newRoom.id }));
      dispatch(setUserAndRoomStatus('fulfilledDataRoom'));
      dispatch(setDataRoom(parsedDateRoom));
      dispatch(setDefaultUserPermissions());

      return parsedDateRoom;
    } catch (e) {
      console.error('Error updating room state:', e);
      return thunkAPI.rejectWithValue('Error updating room state with fetched rooms');
    }
  }
);
